import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Sparkles, Trash2, X, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full rounded-lg border border-[#26201625] bg-white/80 px-3 py-2 text-sm text-[#262016] outline-none focus:border-[#B5793F]";
const labelCls = "block text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] font-medium mb-1.5";
const btnPrimary = { background: "linear-gradient(135deg, #262016 0%, #3B301F 100%)", border: "1px solid #B5793F" };

const PLATFORMS = ["instagram", "linkedin", "youtube", "x"];
const POST_TYPES = ["image", "carousel", "video", "reel", "text"];
const GENRES = { info: "#2E6E8E", tips: "#4E7D4E", humour: "#C2762B", experience: "#7D5BA6", trending: "#C14953", jobs: "#8A6D3B", bts: "#5C5042", announcement: "#B5793F" };
const STYLES = ["ai_generated", "animated", "live_shot", "graphic", "meme"];
const STATUSES = ["idea", "drafted", "ready", "posted"];
const pretty = (s) => (s || "").replace(/_/g, " ");

const api = async (method, path, body) => {
  const r = await fetch(`${API}/api/admin/social${path}`, {
    method, credentials: "include",
    ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Request failed");
  return r.json();
};

function PostEditor({ initial, onSaved, onCancel, onDelete }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const aiWrite = async () => {
    if (!form.idea?.trim()) return toast.error("Type the post idea first — AI writes the copy from it");
    setAiBusy(true);
    try {
      const d = await api("POST", "/ai/copy", { idea: form.idea, platform: form.platform, post_type: form.post_type, genre: form.genre, style: form.style });
      setForm((f) => ({ ...f, copy: d.copy, hashtags: (d.hashtags || []).join(" ") }));
      toast.success("Copy drafted — edit before marking ready");
    } catch (e) { toast.error(e.message); } finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!form.idea?.trim()) return toast.error("Give the post an idea/title");
    setBusy(true);
    try {
      const payload = { ...form, hashtags: typeof form.hashtags === "string" ? form.hashtags.split(/\s+/).filter(Boolean) : form.hashtags || [] };
      if (form.id) await api("PUT", `/${form.id}`, payload); else await api("POST", "", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const sel = (name, options, testid) => (
    <select value={form[name] || options[0]} onChange={(e) => set(name, e.target.value)} className={inputCls} data-testid={testid}>
      {options.map((o) => <option key={o} value={o}>{pretty(o)}</option>)}
    </select>
  );

  return (
    <div className="rp-foam bg-white/70 p-6 mb-6" data-testid="social-editor">
      <div className="flex items-center justify-between mb-5">
        <p className="rp-display text-xl font-medium text-[#262016]">{form.id ? "Edit post" : "New post"} · {form.date}</p>
        <div className="flex items-center gap-2">
          {form.id && (
            <button onClick={() => onDelete(form.id)} className="w-8 h-8 rounded-full border border-[#26201620] flex items-center justify-center text-[#8A7E6B] hover:text-red-700 hover:border-red-700" data-testid="social-delete-btn"><Trash2 size={13} /></button>
          )}
          <button onClick={onCancel} className="text-[#8A7E6B] hover:text-[#262016]" data-testid="social-editor-close"><X size={18} /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div><label className={labelCls}>Date</label><input type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="social-field-date" /></div>
        <div><label className={labelCls}>Platform</label>{sel("platform", PLATFORMS, "social-field-platform")}</div>
        <div><label className={labelCls}>Type</label>{sel("post_type", POST_TYPES, "social-field-type")}</div>
        <div><label className={labelCls}>Genre</label>{sel("genre", Object.keys(GENRES), "social-field-genre")}</div>
        <div><label className={labelCls}>Style</label>{sel("style", STYLES, "social-field-style")}</div>
      </div>
      <div className="mb-4"><label className={labelCls}>Idea — what's this post about?</label>
        <input value={form.idea || ""} onChange={(e) => set("idea", e.target.value)} placeholder="e.g. 3 AI tools I use daily as a designer" className={inputCls} data-testid="social-field-idea" /></div>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] uppercase tracking-[0.14em] text-[#8A7E6B] font-medium">Copy / caption</label>
          <button onClick={aiWrite} disabled={aiBusy} data-testid="social-ai-copy-btn"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#B5793F] border border-[#B5793F55] rounded-full px-3 py-1 hover:bg-[#B5793F12] disabled:opacity-50">
            <Sparkles size={11} /> {aiBusy ? "Writing…" : "Write copy with AI"}
          </button>
        </div>
        <textarea value={form.copy || ""} onChange={(e) => set("copy", e.target.value)} rows={6} className={inputCls} data-testid="social-field-copy" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3 mb-5">
        <div><label className={labelCls}>Hashtags (space separated)</label>
          <input value={Array.isArray(form.hashtags) ? form.hashtags.join(" ") : form.hashtags || ""} onChange={(e) => set("hashtags", e.target.value)} className={inputCls} data-testid="social-field-hashtags" /></div>
        <div><label className={labelCls}>Status</label>{sel("status", STATUSES, "social-field-status")}</div>
      </div>
      <button onClick={save} disabled={busy} data-testid="social-save-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-50" style={btnPrimary}>
        {busy ? "Saving…" : "Save post"}
      </button>
    </div>
  );
}

export default function AdminSocial() {
  const today = new Date();
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiTheme, setAiTheme] = useState("");
  const [aiStart, setAiStart] = useState(today.toISOString().slice(0, 10));
  const [planning, setPlanning] = useState(false);

  const load = useCallback(() => {
    api("GET", `?month=${month}`).then(setPosts).catch(() => setPosts([]));
  }, [month]);
  useEffect(load, [load]);

  const shiftMonth = (d) => {
    const [y, m] = month.split("-").map(Number);
    const nd = new Date(y, m - 1 + d, 1);
    setMonth(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}`);
  };

  const plan = async () => {
    if (!aiTheme.trim()) return toast.error("Describe the week's theme first");
    setPlanning(true);
    try {
      const d = await api("POST", "/ai/plan", { prompt: aiTheme.trim(), start_date: aiStart });
      toast.success(`${d.created} posts planned from ${d.start_date} — tweak anything inline`);
      setAiTheme("");
      setMonth(aiStart.slice(0, 7));
      load();
    } catch (e) { toast.error(e.message); } finally { setPlanning(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this planned post?")) return;
    try { await api("DELETE", `/${id}`); toast.success("Deleted"); setEditing(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [...Array(startPad).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const byDate = {};
  posts.forEach((p) => { (byDate[p.date] = byDate[p.date] || []).push(p); });
  const monthLabel = first.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div data-testid="admin-social">
      <div className="rp-foam bg-white/70 p-5 mb-6">
        <label className={labelCls}><Sparkles size={11} className="inline mr-1 text-[#B5793F]" />Master assistant — AI plans a full week (day, type, genre, style &amp; copy)</label>
        <div className="flex flex-wrap gap-2">
          <input value={aiTheme} onChange={(e) => setAiTheme(e.target.value)} placeholder="Theme — e.g. launch week for my AI design program" className={`${inputCls} flex-1 min-w-[220px]`} data-testid="social-plan-prompt" />
          <input type="date" value={aiStart} onChange={(e) => setAiStart(e.target.value)} className={`${inputCls} w-40`} data-testid="social-plan-start" />
          <button onClick={plan} disabled={planning} data-testid="social-plan-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#F6F1E5] disabled:opacity-60" style={btnPrimary}>
            <CalendarDays size={14} /> {planning ? "Planning… ~20s" : "Plan my week"}
          </button>
        </div>
      </div>

      {editing && <PostEditor initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} onDelete={remove} />}

      <div className="rp-foam bg-white/55 p-5" data-testid="social-calendar">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => shiftMonth(-1)} className="w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center text-[#5C5042] hover:border-[#262016]" data-testid="social-prev-month"><ChevronLeft size={15} /></button>
          <p className="rp-display text-xl font-medium text-[#262016]" data-testid="social-month-label">{monthLabel}</p>
          <button onClick={() => shiftMonth(1)} className="w-9 h-9 rounded-full border border-[#26201620] flex items-center justify-center text-[#5C5042] hover:border-[#262016]" data-testid="social-next-month"><ChevronRight size={15} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1.5 text-center mb-1.5">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <span key={d} className="text-[10px] uppercase tracking-[0.14em] text-[#8A7E6B]">{d}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((day, i) => {
            if (!day) return <div key={`p${i}`} />;
            const date = `${month}-${String(day).padStart(2, "0")}`;
            const dayPosts = byDate[date] || [];
            return (
              <button key={date} onClick={() => setEditing({ date, platform: "instagram", post_type: "image", genre: "info", style: "ai_generated", status: "idea" })}
                      data-testid={`social-day-${date}`}
                      className="min-h-[76px] rounded-lg border border-[#26201614] bg-white/50 p-1.5 text-left hover:border-[#B5793F] transition-colors align-top">
                <span className="block text-[11px] text-[#8A7E6B] mb-1">{day}</span>
                {dayPosts.slice(0, 3).map((p) => (
                  <span key={p.id} role="button" tabIndex={0} data-testid={`social-chip-${p.id}`}
                        onClick={(e) => { e.stopPropagation(); setEditing({ ...p }); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setEditing({ ...p }); } }}
                        className="block w-full truncate rounded px-1.5 py-0.5 mb-1 text-[10px] font-semibold text-white"
                        style={{ background: GENRES[p.genre] || "#5C5042", opacity: p.status === "posted" ? 0.45 : 1 }}
                        title={`${p.platform} · ${pretty(p.post_type)} · ${pretty(p.genre)} · ${p.status}`}>
                    {p.idea || pretty(p.post_type)}
                  </span>
                ))}
                {dayPosts.length > 3 && <span className="text-[10px] text-[#8A7E6B]">+{dayPosts.length - 3} more</span>}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 mt-5">
          {Object.entries(GENRES).map(([g, c]) => (
            <span key={g} className="inline-flex items-center gap-1.5 text-[11px] text-[#5C5042]">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} /> {pretty(g)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
